import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Loader2, RadioTower } from 'lucide-react';
import { assertOfficeScheduleAccess } from '@/lib/auth/officeScheduleGuard';
import { Button } from '@/components/atoms/button';
import { cn } from '@/lib/utils';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import {
  formatElapsedMinutes,
  formatLateness,
  formatMinutesAgo,
  formatVarianceMinutes,
} from '@/lib/time/elapsed';
import { ScheduleWorkspaceShell } from '@/components/layouts';
import { DispatchLiveMap } from '@/components/dispatch/DispatchLiveMap';
import { LiveMetricsBar } from '@/components/dispatch/LiveMetricsBar';
import { DispatchAlertFeed } from '@/components/dispatch/DispatchAlertFeed';
import { RescheduleAlertDialog } from '@/components/dispatch/RescheduleAlertDialog';
import { TodaysSchedulePanel } from '@/components/dispatch/TodaysSchedulePanel';
import { liveStateMeta } from '@/components/dispatch/live-states';
import { findBookingForMapStop } from '@/components/dispatch/map-stop-booking';
import { BookingDetailSheet } from '@/components/calendar/BookingDetailSheet';
import { useAllSchedulerBookings } from '@/hooks/scheduler/useSchedulerBookings';
import type { SchedulerBooking } from '@/types/scheduler';
import {
  useDispatchLive,
  useDispatchAlerts,
  type LiveTech,
  type TechLiveState,
  type DispatchLiveView,
  type DispatchAlert,
} from '@/hooks/scheduler/useDispatchLive';

/** Live states where a tech has a job in flight. */
const ACTIVE_STATES: TechLiveState[] = ['EN_ROUTE', 'ON_SITE', 'WRAPPING_UP'];

/** NOT-334 — the tech list / map filter: All, a single state, or Late. */
type StateFilter = 'ALL' | TechLiveState | 'LATE';

export const Route = createFileRoute('/_dashboard/dashboard/dispatch_/live')({
  beforeLoad: () => assertOfficeScheduleAccess(),
  component: LiveDispatchPage,
});

function minutesAgo(iso: string | null): string {
  if (!iso) return 'never';
  return formatMinutesAgo(Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
}

/** NOT-337 — "today" (YYYY-MM-DD) in the business tz at instant `now`, for the
 *  schedule query. Takes `now` so the business date rolls over past midnight
 *  while the cockpit stays open (instead of being frozen at mount). */
function todayInTz(tz: string, now: number): string {
  const safe = tz && tz !== 'local' ? tz : undefined;
  return new Intl.DateTimeFormat('en-CA', {
    ...(safe ? { timeZone: safe } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(now));
}

/** Live cadence — the cockpit's poll interval, also the clock-tick for
 *  time-dependent UI (date rollover, ageing "Upcoming" jobs). */
const LIVE_TICK_MS = 30_000;

/** Initials for the card avatar (mirrors the board's resource label). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** A short elapsed duration since `iso`: "23m", "1h 5m", "2d 3h". */
function durationSince(iso: string | null): string | null {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 0) return null;
  return formatElapsedMinutes(mins);
}

/** §3 — the state-relevant context line under a tech card. */
function techContext(tech: LiveTech, tz: string): string | null {
  const appt = tech.currentAppointment;
  switch (tech.state) {
    case 'EN_ROUTE': {
      const at = appt?.arrivalTime ?? appt?.scheduledStart;
      return at
        ? `ETA ${formatDateTimeInTimeZone(at, tz, { hour: 'numeric', minute: '2-digit' })}`
        : '—';
    }
    case 'ON_SITE':
    case 'WRAPPING_UP': {
      const d = durationSince(appt?.startedAt ?? appt?.arrivedAt ?? null);
      return d ? `${d} on site` : null;
    }
    case 'AVAILABLE': {
      const d = durationSince(tech.idleSinceAt);
      return d ? `idle ${d}` : null;
    }
    case 'BREAK': {
      // No break-end signal — pings stop while paused, so the last ping ≈ start.
      const d = durationSince(tech.lastSeenAt);
      return d ? `paused ${d}` : null;
    }
    case 'OFFLINE':
      return `last seen ${minutesAgo(tech.lastSeenAt)}`;
    default:
      return null;
  }
}

function LiveDispatchPage() {
  const { data, isLoading } = useDispatchLive();
  const { data: alerts = [] } = useDispatchAlerts();
  const [focusedTechId, setFocusedTechId] = useState<string | null>(null);
  // NOT-336 — the alert whose reschedule drawer is open (null = closed).
  const [rescheduleAlert, setRescheduleAlert] = useState<DispatchAlert | null>(null);
  // NOT-337 — the booking whose detail sheet is open (from the schedule panel).
  const [selectedBooking, setSelectedBooking] = useState<SchedulerBooking | null>(null);
  const [bookingSheetOpen, setBookingSheetOpen] = useState(false);
  // NOT-334 — list/map filter (generalizes NOT-333's behind-only): All, a single
  // live state, or Late. The metrics bar's "Behind" toggle maps to 'LATE'.
  const [stateFilter, setStateFilter] = useState<StateFilter>('ALL');
  // NOT-333 — "Open alerts" metric scrolls the feed into view + flashes it.
  const alertsRef = useRef<HTMLDivElement | null>(null);
  const [alertsFlash, setAlertsFlash] = useState(false);
  // NOT-337 — a clock that ticks on the live cadence so time-dependent UI (the
  // business-date rollover + ageing "Upcoming" jobs) doesn't go stale while the
  // cockpit stays open with no data change.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), LIVE_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const tz = data?.timezone ?? 'local';
  const techs = useMemo(() => data?.techs ?? [], [data]);
  // NOT-336 — technicianId → name, so alert cards can title by tech.
  const techNameById = useMemo(() => new Map(techs.map((t) => [t.technicianId, t.name])), [techs]);

  // NOT-337 — today's jobs for the schedule panel, polled on the live cadence.
  // `now` as a dep rolls the date over past midnight (the string only actually
  // changes at the day boundary, so the query key stays stable within a day).
  const today = useMemo(() => todayInTz(tz, now), [tz, now]);
  // Load EVERY page of today's jobs, not just the first 100 — the live map can
  // draw a pin for any geocoded stop, and "View details" resolves the booking
  // from this list, so a big dispatch day must not leave later pins unresolvable.
  const { data: bookingsResp } = useAllSchedulerBookings(
    { dateFrom: today, dateTo: today },
    { refetchInterval: 30_000 },
  );
  const bookings = useMemo(() => bookingsResp?.data ?? [], [bookingsResp]);
  // Appointments with an open alert → drive the schedule's "Attention" tab.
  const alertApptIds = useMemo(
    () => new Set(alerts.map((a) => a.appointmentId).filter((x): x is string => Boolean(x))),
    [alerts],
  );

  const openBooking = (booking: SchedulerBooking) => {
    setSelectedBooking(booking);
    setBookingSheetOpen(true);
  };

  const isLate = (t: LiveTech) =>
    ACTIVE_STATES.includes(t.state) && t.currentAppointment?.onTime === false;

  const visibleTechs = useMemo(() => {
    if (stateFilter === 'ALL') return techs;
    if (stateFilter === 'LATE') return techs.filter(isLate);
    return techs.filter((t) => t.state === stateFilter);
  }, [techs, stateFilter]);

  // §1 — live per-state counts (+ Late) for the filter chips.
  const counts = useMemo(() => {
    const c = { LATE: techs.filter(isLate).length } as Record<StateFilter, number>;
    for (const t of techs) c[t.state] = (c[t.state] ?? 0) + 1;
    return c;
  }, [techs]);

  const focusAlerts = () => {
    alertsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setAlertsFlash(true);
    window.setTimeout(() => setAlertsFlash(false), 1200);
  };

  return (
    <ScheduleWorkspaceShell>
      <div className="flex h-full min-h-0 flex-col bg-background">
        {/* NOT-333 — summary metrics bar (replaces the thin header). */}
        <LiveMetricsBar
          view={data}
          alertsCount={alerts.length}
          behindOnly={stateFilter === 'LATE'}
          onToggleBehind={() => setStateFilter((f) => (f === 'LATE' ? 'ALL' : 'LATE'))}
          onFocusAlerts={focusAlerts}
          leading={
            <Button asChild variant="ghost" size="icon" className="size-8">
              <Link
                to="/dashboard/dispatch"
                search={{
                  date: data?.timezone ? today : undefined,
                  colorBy: undefined,
                  map: 'off',
                  bookingId: undefined,
                  territoryId: undefined,
                  jobTypeId: undefined,
                  businessUnitId: undefined,
                  priority: undefined,
                  origin: undefined,
                  exceptions: undefined,
                }}
              >
                <ArrowLeft className="size-4" />
              </Link>
            </Button>
          }
        />

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="grid min-h-0 flex-1 grid-rows-[1fr_auto] lg:grid-cols-[minmax(280px,340px)_1fr_minmax(260px,320px)] lg:grid-rows-1">
              {/* Tech status panel */}
              <div className="flex min-h-0 flex-col border-r">
                <StateChips
                  counts={counts}
                  total={techs.length}
                  value={stateFilter}
                  onChange={setStateFilter}
                />
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {visibleTechs.length === 0 ? (
                    <p className="p-6 text-center text-sm text-muted-foreground">
                      {stateFilter === 'ALL'
                        ? 'No active technicians right now.'
                        : 'No technicians match this filter.'}
                    </p>
                  ) : (
                    <ul className="divide-y">
                      {visibleTechs.map((tech) => (
                        <TechRow
                          key={tech.technicianId}
                          tech={tech}
                          variance={data?.variance.byTech.find(
                            (v) => v.technicianId === tech.technicianId,
                          )}
                          tz={tz}
                          focused={tech.technicianId === focusedTechId}
                          onClick={() => setFocusedTechId(tech.technicianId)}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Live map */}
              <div className="relative min-h-[280px] border-r">
                <DispatchLiveMap
                  techs={visibleTechs}
                  focusedTechId={focusedTechId}
                  onSelectTech={setFocusedTechId}
                  onViewJob={(appointmentId) => {
                    const booking = findBookingForMapStop(bookings, appointmentId);
                    if (booking) openBooking(booking);
                  }}
                  onClose={() => setFocusedTechId(null)}
                  tz={tz}
                />
              </div>

              {/* Alert feed */}
              <div
                ref={alertsRef}
                className={cn(
                  'min-h-0 overflow-y-auto transition-colors duration-300',
                  alertsFlash && 'bg-destructive/5 ring-1 ring-inset ring-destructive/30',
                )}
              >
                <DispatchAlertFeed
                  alerts={alerts}
                  techNameById={techNameById}
                  onReschedule={setRescheduleAlert}
                  minutesAgo={minutesAgo}
                />
              </div>
            </div>
            {/* NOT-337 — Today's schedule strip (full-width, below the cockpit). */}
            <TodaysSchedulePanel
              bookings={bookings}
              alertApptIds={alertApptIds}
              tz={tz}
              now={now}
              variant="strip"
              onSelect={openBooking}
            />
          </>
        )}
      </div>

      {rescheduleAlert?.jobId && rescheduleAlert.appointmentId ? (
        <RescheduleAlertDialog
          jobId={rescheduleAlert.jobId}
          appointmentId={rescheduleAlert.appointmentId}
          onClose={() => setRescheduleAlert(null)}
        />
      ) : null}

      {/* NOT-337 — visit detail from a schedule row/chip. */}
      <BookingDetailSheet
        booking={selectedBooking}
        open={bookingSheetOpen}
        onOpenChange={(next) => {
          setBookingSheetOpen(next);
          if (!next) setSelectedBooking(null);
        }}
      />
    </ScheduleWorkspaceShell>
  );
}

function TechRow({
  tech,
  variance,
  tz,
  focused,
  onClick,
}: {
  tech: LiveTech;
  variance?: DispatchLiveView['variance']['byTech'][number];
  tz: string;
  focused: boolean;
  onClick: () => void;
}) {
  const meta = liveStateMeta(tech.state);
  const appt = tech.currentAppointment;
  const context = techContext(tech, tz);
  const jobLine = appt
    ? [appt.serviceType?.name, appt.customerName].filter(Boolean).join(' · ')
    : 'No active job';
  // §15 — surface a stale-GPS warning whenever the ping is old, independent of
  // the derived state: deriveState still returns EN_ROUTE/ON_SITE for an active
  // job before considering staleness, so without this an old ping would show a
  // confident ETA with no hint the location isn't current. OFFLINE already says
  // "last seen …" in its context line, so skip the duplicate there.
  const staleHint = tech.stale && tech.state !== 'OFFLINE';
  // Sharing observability: on an ACTIVE job, distinguish *why* the dot isn't
  // live — signal lost (app closed / shift auto-closed at the 16h cap) vs never
  // shared vs paused — so a frozen marker never reads as en route. When the API
  // predates the `sharing` field (reason == null), fall back to the plain stale
  // hint so an old ping still warns.
  const reason = tech.sharing?.reason;
  const hasActiveJob = !!appt;
  const gpsWarn =
    reason == null ? staleHint : (reason === 'stale' || reason === 'not_sharing') && hasActiveJob;
  // Paused is intentional (no amber), but its dot is still not live — so the
  // tooltip reads "unavailable", not "last ping".
  const gpsUnavailable = gpsWarn || reason === 'paused';
  // Only append a relative age when the ping is genuinely in the PAST. A
  // future-dated ping (bad device clock) is flagged stale by the backend but
  // minutesAgo() would say "just now" — "Signal lost · just now" is nonsense, so
  // drop the suffix for that case.
  const ageSeconds = tech.sharing?.ageSeconds ?? null;
  const lostAgo = ageSeconds != null && ageSeconds > 0 ? ` · ${minutesAgo(tech.lastSeenAt)}` : '';
  const gpsLabel =
    hasActiveJob && reason === 'not_sharing'
      ? 'Not sharing'
      : hasActiveJob && reason === 'stale'
        ? `Signal lost${lostAgo}`
        : reason === 'paused'
          ? 'Paused'
          : tech.lastSeenAt
            ? minutesAgo(tech.lastSeenAt)
            : '—';
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50',
          focused && 'bg-primary/5',
        )}
      >
        {/* Avatar + live-state dot */}
        <span className="relative shrink-0">
          <span className="flex size-9 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">
            {initials(tech.name)}
          </span>
          <span
            className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-background"
            style={{ backgroundColor: meta.color }}
            title={meta.label}
          />
        </span>

        {/* Middle — identity + current job + context */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{tech.name}</p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{jobLine}</p>
          {context ? (
            <p className="mt-0.5 truncate text-[11px] font-medium text-foreground tabular-nums">
              {context}
            </p>
          ) : null}
          {/* §15.3 — today's variance (end/duration appear once a job completes). */}
          {variance && variance.appointments > 0 ? (
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground tabular-nums">
              <span>start {formatVarianceMinutes(variance.netStartVarianceMin)}</span>
              {variance.netEndVarianceMin != null ? (
                <span>end {formatVarianceMinutes(variance.netEndVarianceMin)}</span>
              ) : null}
              {variance.netDurationVarianceMin != null ? (
                <span>dur {formatVarianceMinutes(variance.netDurationVarianceMin)}</span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Right — status, on-time, last GPS ping (aligned column) */}
        <div className="flex shrink-0 flex-col items-end gap-1 pl-1">
          <span className="text-[11px] font-semibold" style={{ color: meta.color }}>
            {meta.label}
          </span>
          {appt?.lateMinutes != null && appt.lateMinutes > 0 ? (
            <span className="rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
              {formatLateness(appt.lateMinutes)}
            </span>
          ) : appt?.onTime ? (
            <span className="rounded-full bg-emerald-100 px-1.5 py-px text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
              On time
            </span>
          ) : null}
          <span
            className={cn(
              'flex items-center gap-1 text-[10px] tabular-nums',
              gpsWarn ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-muted-foreground',
            )}
            title={gpsUnavailable ? 'Live location unavailable' : 'Last GPS ping'}
          >
            <RadioTower className="size-3" />
            {gpsLabel}
          </span>
        </div>
      </button>
    </li>
  );
}

/** §1 — live state filter chips (counts hide at 0, except All). */
const CHIP_ORDER: { key: StateFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'EN_ROUTE', label: 'En route' },
  { key: 'ON_SITE', label: 'On site' },
  { key: 'WRAPPING_UP', label: 'Wrapping up' },
  { key: 'AVAILABLE', label: 'Available' },
  { key: 'LATE', label: 'Late' },
  { key: 'BREAK', label: 'Break' },
  { key: 'OFFLINE', label: 'Offline' },
];

function StateChips({
  counts,
  value,
  onChange,
  total,
}: {
  counts: Record<StateFilter, number>;
  value: StateFilter;
  onChange: (next: StateFilter) => void;
  total?: number;
}) {
  return (
    <div className="flex shrink-0 flex-wrap gap-1.5 border-b px-3 py-2">
      {CHIP_ORDER.map(({ key, label }) => {
        const count = key === 'ALL' ? (total ?? 0) : (counts[key] ?? 0);
        const selected = value === key;
        // Hide an empty state chip (less noise); All always shows. Keep the
        // SELECTED chip even at 0 — if a live refresh empties it (e.g. the last
        // late tech recovers), hiding it would orphan the filtered-empty list;
        // showing "Late · 0" + the empty state reads as "nobody's late now".
        if (key !== 'ALL' && count === 0 && !selected) return null;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={selected}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors',
              selected
                ? 'border-foreground bg-foreground text-background'
                : 'border-border text-muted-foreground hover:bg-muted/60',
            )}
          >
            {label}
            <span className="tabular-nums opacity-70">{count}</span>
          </button>
        );
      })}
    </div>
  );
}
